import type { VendorDocumentDTO } from '@deepguide-ai/types'

/**
 * RFC-231 §3.1 — the row model behind the single "documents for this review"
 * list, kept pure so it can be tested without React or tRPC.
 *
 * The tier checklist (RFC-196 evidence requests) and the document selection
 * (RFC-198 assessment inputs) answer one question — what does this review have,
 * and what is missing — so they render as one list rather than two panels that
 * can disagree.
 */

export type EvidenceRequest = {
  id: string
  docType: string
  label: string
  required: boolean
  status: 'OUTSTANDING' | 'RECEIVED' | 'WAIVED'
  receivedAt?: string | Date | null
  receivedDocId?: string | null
  waivedReason?: string | null
}

export type VendorDocument = Pick<VendorDocumentDTO, 'id' | 'documentType' | 'title' | 'fileName'> & Partial<VendorDocumentDTO>

export type DocumentRow = {
  key: string
  request?: EvidenceRequest
  source?: { docType: string; label: string }
  doc?: VendorDocument
}

export function getEvidenceCompleteness(requests: EvidenceRequest[]) {
  const total = requests.length
  const received = requests.filter((r) => r.status === 'RECEIVED').length
  const waived = requests.filter((r) => r.status === 'WAIVED').length
  const outstanding = requests.filter((r) => r.status === 'OUTSTANDING').length
  const requiredRequests = requests.filter((r) => r.required)
  const requiredOutstanding = requiredRequests.filter(
    (r) => r.status === 'OUTSTANDING'
  ).length
  const complete = total > 0 ? received + waived : 0
  return {
    total,
    received,
    waived,
    outstanding,
    requiredTotal: requiredRequests.length,
    requiredOutstanding,
    // Mirrors hasCompleteRequiredVendorEvidence: nothing required outstanding
    // is complete, including the case where nothing is required at all.
    requiredComplete: requiredOutstanding === 0,
    percent: total > 0 ? Math.round((complete / total) * 100) : 0,
  }
}

/**
 * One row per expected document or source, then one per additional file. A request takes its `receivedDocId` when set, else the first
 * unclaimed stash document of the same type — the rule the server reconciles
 * with. Each document is claimed at most once, so two requests of the same type
 * never show the same file.
 */
export function buildDocumentRows(
  requests: EvidenceRequest[],
  documents: VendorDocument[],
  sources: Record<string, string> = {},
): DocumentRow[] {
  const claimed = new Set<string>()
  const requestRows = requests.map((request) => {
    const doc =
      documents.find((d) => d.id === request.receivedDocId) ??
      documents.find(
        (d) => d.documentType === request.docType && !claimed.has(d.id)
      )
    if (doc) claimed.add(doc.id)
    return { key: request.id, request, doc }
  })
  const sourceRows = Object.entries(sources)
    .filter(([docType]) => !requests.some(request => request.docType === docType))
    .map(([docType, label]) => {
      const doc = documents.find(document => document.documentType === docType && !claimed.has(document.id))
      if (doc) claimed.add(doc.id)
      return { key: `source:${docType}`, source: { docType, label }, doc }
    })
  const extraRows = documents
    .filter((d) => !claimed.has(d.id))
    .map((doc) => ({ key: doc.id, doc }))
  return [...requestRows, ...sourceRows, ...extraRows]
}
